import { specValue } from '../cars/carFigures';
import {
    baseDirection,
    beatsOn,
    cardValue,
    invertDirection,
    isPlayableOn,
} from './overtakeCards';
import { SIDES, cardOf } from './overtakeDeck';
import {
    availableSpecs,
    canCounterCall,
    canVeto,
    leadableCards,
    legalPlays,
    standingCard,
} from './overtakeMatch';

/**
 * Somebody to play against (SPEC-011 PR-11.2).
 *
 * A pure function that is handed a match and answers a MOVE — never a match.
 * `application/game/useOvertakeMatch.js` applies it, and owns the delay that
 * makes it feel like somebody thinking, for the same reason the duel's clock
 * lives in the application layer rather than in `duelRun` (SPEC-011 D-13).
 *
 * There is no search here and there is not going to be one (SPEC-011 D-12). A
 * fourteen-card game against a solver is not fun, and `pro`'s interesting
 * failure — that it will not bluff, so a cheap card led confidently reads to it
 * as a cheap card — is a better opponent than a perfect one.
 *
 * What it may legitimately reason about is the DECK, which is public: this is a
 * catalogue the reader can open in another tab. What it must never read is the
 * other hand, and it never does — every function below takes the match and looks
 * only at its own side, the table, and the cards nobody has seen.
 */

export const OPPONENT_SKILLS = ['rookie', 'pro'];

/** Clamped for the same reason `shuffle` clamps: a double may answer exactly 1. */
function pick(list, random) {
    return list.length === 0
        ? null
        : list[Math.min(Math.floor(random() * list.length), list.length - 1)];
}

/**
 * The cards nobody at the table has seen.
 *
 * Everything in the deck, less this side's own hand, less both pots, less what
 * is on the table. That is the stock plus the other hand, and the opponent
 * cannot tell those apart — which is exactly the uncertainty a human plays
 * under, so reasoning about the set as a whole is fair rather than a peek.
 */
function unseenIds(match, deck, side) {
    const accounted = new Set([
        ...(match.hands?.[side] ?? []),
        ...SIDES.flatMap((each) => match.pots?.[each] ?? []),
        ...(match.pile ?? []).map((played) => played.id),
    ]);

    return [...deck.keys()].filter((id) => !accounted.has(id));
}

/** How many of these cards would beat `car` on this figure. Zero is unbeatable. */
function threatsTo(car, ids, deck, spec, direction) {
    return ids.filter((id) => beatsOn(cardOf(deck, id), car, spec, direction)).length;
}

/** How far this card clears the one on the table. Smaller is less wasteful. */
function marginOver(car, standing, spec) {
    const attacking = specValue(car, spec);
    const defending = specValue(standing, spec);

    return attacking === null || defending === null ? Infinity : Math.abs(attacking - defending);
}

/**
 * The cheapest card that beats, and among equals the one that clears by least.
 *
 * The whole of `pro`'s answering game, and the correct play in this ruleset for
 * the reason SPEC-011 D-6 lays out: every card put down is at stake, so an
 * overtake that wins by a hundred horsepower and an overtake that wins by two
 * are worth the same pot and cost very different cards.
 */
function cheapestThatBeats(match, deck) {
    const standing = standingCard(match, deck);

    return legalPlays(match, deck).reduce((best, id) => {
        if (!best) {
            return id;
        }

        const held = cardOf(deck, best);
        const trying = cardOf(deck, id);
        const byValue = cardValue(trying) - cardValue(held);

        if (byValue !== 0) {
            return byValue < 0 ? id : best;
        }

        return marginOver(trying, standing, match.spec) < marginOver(held, standing, match.spec)
            ? id
            : best;
    }, null);
}

/**
 * Open a round.
 *
 * `pro` names the figure it is strongest in and then leads its SECOND-best card
 * on it, keeping the best back to overtake with — which is the difference
 * between a lead and a surrender. With only one playable card on that figure it
 * leads that one, because a figure it cannot follow up on is still a figure the
 * reader has to answer.
 */
function chooseLead(match, deck, skill, random) {
    const side = match.lead;
    const specs = availableSpecs(match)
        .filter((spec) => leadableCards(match, deck, spec).length > 0);

    if (specs.length === 0) {
        return null;
    }

    if (skill !== 'pro') {
        const spec = pick(specs, random);

        return { type: 'lead', carId: pick(leadableCards(match, deck, spec), random), spec };
    }

    const unseen = unseenIds(match, deck, side);
    // Strongest means fewest cards out there that could answer it.
    const best = specs
        .map((spec) => {
            const direction = baseDirection(spec);
            const ranked = [...leadableCards(match, deck, spec)].sort(
                (one, other) => threatsTo(cardOf(deck, one), unseen, deck, spec, direction)
                    - threatsTo(cardOf(deck, other), unseen, deck, spec, direction),
            );

            return {
                spec,
                direction,
                ranked,
                threats: threatsTo(cardOf(deck, ranked[0]), unseen, deck, spec, direction),
            };
        })
        .reduce((strongest, each) => (each.threats < strongest.threats ? each : strongest));

    // The bait: the second-strongest card, so the strongest is still in hand when
    // the reader commits to answering.
    const carId = best.ranked[1] ?? best.ranked[0];

    return {
        type: 'lead',
        carId,
        spec: best.spec,
        // Doubling only on a card nothing unseen can answer. It is still a risk —
        // a counter-call turns the figure over and this read with it — which is
        // what keeps the power interesting rather than a tell.
        doubled: threatsTo(cardOf(deck, carId), unseen, deck, best.spec, best.direction) === 0,
    };
}

/**
 * Answer a figure — or refuse it, invert it, or give it up.
 *
 * The order is the order of cost. Playing a card is the cheapest answer and is
 * tried first; a counter-call spends a power to make a card playable; a veto
 * spends a power to make the figure go away; and passing spends the pot.
 */
function chooseAnswer(match, deck, skill, random) {
    const plays = legalPlays(match, deck);

    if (plays.length > 0) {
        return {
            type: 'play',
            carId: skill === 'pro' ? cheapestThatBeats(match, deck) : pick(plays, random),
        };
    }

    if (skill !== 'pro') {
        return { type: 'pass' };
    }

    // Nothing beats. Would turning the figure over change that? The inversion
    // binds this side too, so it is only worth spending where it produces a card
    // that can actually go down.
    const standing = standingCard(match, deck);
    const inverted = invertDirection(match.direction);
    const rescued = canCounterCall(match)
        && (match.hands[match.turn] ?? [])
            .some((id) => beatsOn(cardOf(deck, id), standing, match.spec, inverted));

    if (rescued) {
        return { type: 'counter' };
    }

    // Hopeless rather than merely bad: the card on the table is one of the three
    // best in the whole deck on this figure, so no plausible draw rescues it.
    // Handing the naming back costs the leader their pick and is worth a power.
    const hopeless = threatsTo(standing, [...deck.keys()], deck, match.spec, match.direction) < 3;

    return canVeto(match) && hopeless ? { type: 'veto' } : { type: 'pass' };
}

/** Name a different figure for the card already on the table, after a veto. */
function chooseRename(match, deck, skill, random) {
    const led = cardOf(deck, match.pile[0]?.id);
    const specs = availableSpecs(match)
        .filter((spec) => spec !== match.vetoed && isPlayableOn(led, spec));

    if (specs.length === 0) {
        return { type: 'pass' };
    }

    if (skill !== 'pro') {
        return { type: 'rename', spec: pick(specs, random) };
    }

    const unseen = unseenIds(match, deck, match.lead);
    const spec = specs.reduce((best, each) => (
        threatsTo(led, unseen, deck, each, baseDirection(each))
            < threatsTo(led, unseen, deck, best, baseDirection(best)) ? each : best));

    return { type: 'rename', spec };
}

/**
 * What this opponent does now, or null where it is not their move.
 *
 * Null rather than a no-op move, so a caller that asks at the wrong moment gets
 * an answer it has to handle rather than one it can apply. The three statuses
 * with no move in them are the two endings and the round result, and all three
 * are the application layer's to advance.
 */
export function chooseMove(match, deck, { side = 'opponent', skill = 'pro', random = Math.random } = {}) {
    const playing = OPPONENT_SKILLS.includes(skill) ? skill : 'pro';

    if (!match || match.turn !== side) {
        return null;
    }

    if (match.status === 'leading') {
        return chooseLead(match, deck, playing, random);
    }

    if (match.status === 'renaming') {
        return chooseRename(match, deck, playing, random);
    }

    return match.status === 'answering' ? chooseAnswer(match, deck, playing, random) : null;
}
